// FLEET LIVE's sound cues (#6779, prototype `:3540-3599`): `on` (a panel shown), `off`
// (hidden), `dock` (a panel placed), `lift` (a menu opened, a drag started or crossing a
// zone), `pipin` and `pipout` (the floating Core in and out). Built on the notification
// sound's `SoundRuntime` seam (`notification-sound.ts`): the runtime is created once and
// reused, and a context still suspended (no gesture yet) plays nothing and throws nothing.
//
// THE SWITCH IS A CONTRACT: `fleet-live-sound`, stored through `local-pref.ts` as `'on' |
// 'off'`, absent meaning on. It mutes every Fleet page sound, these cues and #6773's transfer
// sounds, which read the same key through `readLocalPref` themselves, so neither the key nor
// its two values may be renamed. The notification bell is not a page sound and keeps its own
// setting (`dz.notification-sound`).
//
// TONES AND SWEEPS ONLY (brief.md item 73): the prototype's cues layer filtered noise, a
// resonant low-pass and a delay line over a compressor; here each is approximated with
// oscillator tones, some sweeping their pitch (`toFrequency`), each at most 0.15 peak gain
// and ending within 700 ms. The bounds are pinned by literals in the test.

import { z } from 'zod';
import { readLocalPref, writeLocalPref } from './local-pref';
import { browserSoundRuntime, type SoundRuntime } from './notification-sound';

export const FLEET_SOUND_KEY = 'fleet-live-sound';

export const FLEET_CUES = ['on', 'off', 'dock', 'lift', 'pipin', 'pipout'] as const;
export type FleetCue = (typeof FLEET_CUES)[number];

const SoundSchema = z.enum(['on', 'off']);

/** Whether Fleet page sounds play: every cue checks it, and #6773 reads the same key. */
export function fleetSoundOn(): boolean {
  return readLocalPref(FLEET_SOUND_KEY, SoundSchema, 'on') !== 'off';
}

export function writeFleetSound(on: boolean): void {
  writeLocalPref<'on' | 'off'>(FLEET_SOUND_KEY, on ? 'on' : 'off');
}

export type CueTone = Parameters<SoundRuntime['playTone']>[1];

const tone = (
  type: CueTone['type'],
  frequency: number,
  toFrequency: number,
  startMs: number,
  stopMs: number,
  peak: number,
): CueTone => ({ type, frequency, toFrequency, startMs, stopMs, peak });

const CUES: Readonly<Record<FleetCue, readonly CueTone[]>> = {
  on: [
    tone('sine', 55, 110, 0, 350, 0.15),
    tone('sawtooth', 140, 880, 0, 500, 0.05),
    tone('triangle', 880, 884, 200, 620, 0.075),
    tone('triangle', 1318.5, 1323.8, 270, 690, 0.075),
    tone('triangle', 1760, 1767, 340, 700, 0.075),
    tone('triangle', 2637, 2647.5, 410, 700, 0.06),
  ],
  off: [
    tone('sawtooth', 900, 70, 0, 550, 0.06),
    tone('sine', 440, 48, 0, 620, 0.15),
    tone('square', 2200, 2100, 500, 540, 0.035),
    tone('sine', 60, 40, 500, 700, 0.15),
  ],
  dock: [
    tone('triangle', 660, 990, 0, 120, 0.09),
    tone('triangle', 990, 1480, 90, 250, 0.08),
    tone('sine', 120, 70, 0, 180, 0.15),
  ],
  lift: [tone('triangle', 520, 780, 0, 100, 0.06)],
  pipin: [
    tone('triangle', 440, 1320, 0, 200, 0.07),
    tone('sine', 1760, 1760, 120, 470, 0.035),
    tone('sine', 2637, 2630, 180, 580, 0.025),
    tone('sine', 80, 120, 0, 180, 0.15),
  ],
  pipout: [tone('triangle', 1320, 330, 0, 220, 0.06), tone('sine', 110, 50, 50, 250, 0.15)],
};

/** A cue's tones. */
export function fleetCueTones(cue: FleetCue): readonly CueTone[] {
  return CUES[cue];
}

/**
 * Play `cue` once, unless the switch is off. Answers whether it played; never rejects. A
 * context that is not running is asked to resume for the next cue and this one is dropped:
 * a cue is feedback on the click that made it, never worth playing late.
 */
export async function playFleetCue(
  cue: FleetCue,
  runtime: SoundRuntime = browserSoundRuntime(),
): Promise<boolean> {
  if (!fleetSoundOn()) return false;
  let ctx: unknown;
  try {
    ctx = runtime.create();
  } catch {
    return false;
  }
  if (runtime.state(ctx) !== 'running') {
    runtime.resume(ctx).catch(() => {});
    return false;
  }
  try {
    for (const spec of CUES[cue]) runtime.playTone(ctx, spec);
  } catch {
    return false;
  }
  return true;
}
